import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { GET_PASSWORDS, VALIDATE_PASSWORD } from "../../api/password";
import { decryptData } from "../../utils/decrypt.utils";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "400px" },
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
  backdropFilter: "blur(89px)",
  outline: "none",
};

const PasswordModal = ({ onClose, onSuccess, path }) => {
  const [passwordInput, setPasswordInput] = useState("");
  const [correctPassword, setCorrectPassword] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const isAuthed = localStorage.getItem(`auth_${path}`);
    if (isAuthed === "true") {
      onSuccess();
      onClose();
      return;
    }

    const fetchPassword = async () => {
      try {
        const res = await axios.get(GET_PASSWORDS + "?verify=true");
        const matches = res.data.find((item) => item.path === path);
        if (matches && matches.password) {
          const decryptedPassword = decryptData(matches.password);
          setCorrectPassword(decryptedPassword || "ThisIsOurProduct");
        } else {
          setCorrectPassword("ThisIsOurProduct");
        }
      } catch (err) {
        console.error("Password fetch error:", err);
        setCorrectPassword("ThisIsOurProduct");
      } finally {
        setLoading(false);
      }
    };

    fetchPassword();
  }, [path, onClose, onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple front-end validation
    if (!passwordInput) {
      setError("Please enter the password.");
      return;
    }

    setError(""); // Clear previous error

    try {
      const response = await axios.post(VALIDATE_PASSWORD, {
        password: passwordInput,
        path,
      });

      const data = response.data;

      if (data.status === true) {
        localStorage.setItem(`auth_${path}`, "true");
        onSuccess();
        onClose();
      } else {
        setError(data.error || "Incorrect password. Please try again.");
        setPasswordInput("");
      }
    } catch (err) {
      console.error("API Error:", err);
      setError(
        err?.response?.data?.error ??
          "Something went wrong. Please try again later."
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        },
      }}
    >
      <Box sx={modalStyle}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "grey.600" }}
        >
          <CloseIcon />
        </IconButton>

        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" mt={2}>
              Verifying access permissions...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Protected Page
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              This page requires a password to view the content.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type="password"
                label="Enter Password"
                variant="outlined"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!passwordInput.trim()}
                sx={{
                  backgroundColor: "#6a58c6",
                  color: "#fff",
                  fontWeight: "bold",
                  textTransform: "none",
                  borderRadius: 2,
                  py: 1.5,
                }}
              >
                Unlock Content
              </Button>
            </form>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default PasswordModal;
